import { IReorderable } from './../../tools/reordering/reorderable.interface';
import { RequestOptions } from './../../services/base.service';
import { SettingsPopUpService } from './../../settings-pop-up.service';
import { inject, Injectable } from '@angular/core';
import { BaseService } from 'src/app/services/base.service';
import { BehaviorSubject,  Observable, of, shareReplay, tap } from 'rxjs';
import { List } from 'src/app/models/list';
import { Role } from 'src/app/models/role';
import { TableString } from 'src/app/models/table-string.model';
import { GroupData } from '../forecast/activity/models/activity-data.model';
import { ReturnFlag, ReturnFlags } from 'src/app/models/return-flag';
import { ForecastElementDataNew, UpdateForecastElementData } from 'src/app/models/forecast/fc-validationlog-model';
import { NextPreAction, NextPreValue } from 'src/app/models/forecast/NextPrevious';
import { PivotSettingParam } from 'src/app/models/forecast/fc-pivot-model';
import { SelectStatementResult } from 'src/app/models/select-statement-result.model';
import { QueryFilterParameter, QueryParameter } from 'src/app/widgets/filters/models/query-filter-parameter.model';
import { ServiceLocator } from 'src/app/tools/service-locator.tool';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { BookMark, BookmarkData } from 'src/app/models/Bookmark';
import { SessionStorageService } from 'src/app/services/SessionStorageService';
import { WebCaches } from 'src/app/enums/webCachesEnum';

@Injectable({
	providedIn: 'root'
})
export class SettingsService {

	private static readonly REQUEST_TOP_PATH: string = "settings";

	private _baseService: BaseService = inject(BaseService);

	private settingData$: Observable<any>;

	public clearCache(requestOptions?: RequestOptions): Observable<any> {
		const requestEndpoint: string = "ResetCache";

		let url = `${SettingsService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	public getSelections(selectionSetId: number, requestOptions?: RequestOptions): Observable<SelectionData> {
		let url = `settings/GetSelections?selectionSetId=${selectionSetId}`;

		return this._baseService.get(url, requestOptions);
	}

	public getFcEsoPageMappings(filters: QueryFilterParameter[], requestOptions?: RequestOptions): Observable<any> {
		let url = `settings/GetFcEsoPageMappingsNew`;

		let queryParameters: QueryParameter[] = [];

		filters.forEach(
			filter => {
				let item: QueryParameter = { fieldName: filter.fieldName, operation: filter.operation, value: decodeURIComponent(filter.value) };

				queryParameters.push(item);
			}
		);

		return this._baseService.post(url, queryParameters, requestOptions);

	}

	public saveSelections(selectionSetId: number, selections: SelectionOutput[], requestOptions?: RequestOptions): Observable<any> {
		let url = 'settings/SaveSelections';

		return this._baseService.post(url, { selectionSet: selectionSetId, selections: selections }, requestOptions);
	}

	getGeneralSettings(requestOptions?: RequestOptions): Observable<GeneralSettings> {
		let url = 'settings/GetGeneralSettings';
		if (!this.settingData$) {
			this.settingData$ = this._baseService.get(url, requestOptions).pipe(shareReplay(1));
		}
		return this.settingData$;
	}

	getGeneralSettingsLocalization(requestOptions?: RequestOptions): Observable<GeneralSetting> {
		const cachedData = localStorage.getItem(WebCaches.LocalizationLng);
		if (cachedData) {
			return of(JSON.parse(cachedData));
		}
		let url = 'settings/WebSettings?action='+ encodeURIComponent('Localization value');

		return this._baseService.post<GeneralSetting>(url, requestOptions).pipe(
			tap(data => {
			 localStorage.setItem(WebCaches.LocalizationLng, JSON.stringify(data));
			}));
	}

	GetGeneralSettingsNew(): Observable<GeneralSettings> {
		let url = 'settings/GetGeneralSettingsNew';
		return this._baseService.get(url);
	}

	AllEsoSettings(): Observable<GeneralSettings> {
		let url = 'settings/GetAllEsoSettings';
		return this._baseService.get(url);
	}

	public saveGeneralSettings(settings: GeneralSettingsOutput[], requestOptions?: RequestOptions): Observable<any> {
		let url = 'settings/SaveGeneralSettings';

		return this._baseService.post(url, settings, requestOptions);
	}

	public getRoles(requestOptions?: RequestOptions): Observable<Role[]> {
		const requestEndpoint: string = "GetRoles";

		let url = `${SettingsService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	public getMenuItemsByRole(roleId: number, requestOptions?: RequestOptions): Observable<RoleMenu[]> {
		const requestEndpoint: string = "GetMenuItemsByRole";

		let url = `${SettingsService.REQUEST_TOP_PATH}/${requestEndpoint}?roleId=${roleId}`;

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	public getMenuItemsByRoleDashboard(roleId: number, requestOptions?: RequestOptions): Observable<RoleMenu[]> {
		const requestEndpoint: string = "GetMenuItemsByRoleDashboard";

		let url = `${SettingsService.REQUEST_TOP_PATH}/${requestEndpoint}?roleId=${roleId}`;

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	public saveMenuItemsByRole(menuItems: RoleMenu[], roleId: number, requestOptions?: RequestOptions): Observable<any> {
		let menuItemsByRole = new MenuItemsByRole(menuItems, roleId);

		const requestEndpoint: string = "SaveMenuItemsByRole";

		let url = `${SettingsService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.post(
			url,
			menuItemsByRole,
			requestOptions
		);
	}

	getContentData(): Observable<ContentItem[]> {
		let url = 'settings/GetContentData';
		return this._baseService.get(url);
	}

	getDynamicTables(action: string): Observable<any> {
		let url = 'settings/SelectRoleAccessData?action=' + action;
		return this._baseService.post(url);
	}

	GetContentItemsByObject(grouptype: string, groupname: string, id: string): Observable<any> {
		let url = 'settings/GetContentItemsByObject';
		return this._baseService.post(url, { grouptype: grouptype, groupname: groupname, id: id });
	}

	getContentPermissionsByRole(roleId: number): Observable<ContentPermission[]> {
		let url = `settings/GetContentPermissionsByRole?roleId=${roleId}`;
		return this._baseService.get(url);
	}

	getObjectContentPermissionsByRole(roleId: number, grouptype: string, groupdata: string): Observable<ContentPermission[]> {
		let url = `settings/GetObjectContentPermissionsByRole?roleId=${roleId}&grouptype=${grouptype}&groupdata=${groupdata}`;
		return this._baseService.post(url);
	}

	saveContentPermissionsByRole(permissions: ContentPermission[], roleId: number, Data: string,
		GroupType: string): Observable<any> {
		let url = 'settings/SaveContentPermissionsByRole';
		let contentPermissionsByRole = new ContentPermissionsByRole(permissions, roleId, Data, GroupType);
		return this._baseService.post(url, contentPermissionsByRole);
	}

	//#region <Core Settings>
	private readonly _getCoreSettingTypesRequestEndpoint: string = "GetCoreSettingTypes";

	public getCoreSettingTypes(): Observable<CoreSettingType[]> {
		let url = `settings/${this._getCoreSettingTypesRequestEndpoint}`;

		return this._baseService.get(url);
	}

	private _coreSettingTypesMap: Map<number, string> = new Map<number, string>();

	private convertStringToMap(str: string): Map<number, string> {
		const json = JSON.parse(str);
		const map = new Map<number, string>();
		for (const key in json) {
			if (json.hasOwnProperty(key)) {
				map.set(Number(key), json[key]);
			}
		}

		return map;
	}

	private _initializeCoreSettingTypesMapPromise: Promise<CoreSettingType[]> = null;
	private _coreSettingTypesMapCacheName = "_coreSettingTypesMap";

	public async initializeCoreSettingTypesMap(): Promise<CoreSettingType[]> {
		const $this = this;
		if ($this.localStorageService.get($this._coreSettingTypesMapCacheName)) {
			let SettingTypesMap = JSON.parse($this.localStorageService.get($this._coreSettingTypesMapCacheName));
			if ($this._coreSettingTypesMap && $this._coreSettingTypesMap.values.length == 0) {
				for (let a = 0; a < SettingTypesMap.length; a++) {
					$this._coreSettingTypesMap.set(SettingTypesMap[a].Id, SettingTypesMap[a].Name);
				}
			}
			return Promise.resolve(SettingTypesMap);
		}
		else {
			const observable$ = this.getCoreSettingTypes();
			observable$.subscribe(
				(response) => {
					for (let a = 0; a < response.length; a++) {
						$this._coreSettingTypesMap.set(response[a].Id, response[a].Name);
					}
					$this.localStorageService.set($this._coreSettingTypesMapCacheName, JSON.stringify(response));
					this._initializeCoreSettingTypesMapPromise = Promise.resolve(response);
				},
				(error) => {
					console.error(error);

				}
			);

			this._initializeCoreSettingTypesMapPromise = observable$.toPromise();
			console.log("this._initializeCoreSettingTypesMapPromise", this._initializeCoreSettingTypesMapPromise);
			return this._initializeCoreSettingTypesMapPromise;
		}
	}

	public static readonly DEFAULT_SETTINGS_GROUP_NAME = "General";

	private initializeCoreSettingsWithNames(sourceGroupName: string, ...coreSettings: CoreSetting[]) {
		for (let a = 0; a < coreSettings.length; a++) {
			coreSettings[a].settingTypeName = this._coreSettingTypesMap.get(coreSettings[a].settingTypeId);
			coreSettings[a].sourceGroupName = sourceGroupName;
		}
	}

	private readonly _getCoreSettingRequestEndpoint: string = "GetCoreSetting";

	public getCoreSetting(id: number, name: string, addToSettingsPopUp: boolean = true, requestOptions: RequestOptions = RequestOptions.DEFAULT): Promise<CoreSetting> {
		let url = `settings/${this._getCoreSettingRequestEndpoint}?name=${name}` + (id ? `&id=${id}` : "");

		const $this = this;

		return new Promise(
			(resolve, reject) => {
				const observable$: Observable<CoreSetting> = this._baseService.get(url, requestOptions);

				observable$.subscribe(
					{
						next: (coreSetting) => {

							coreSetting = Object.assign(new CoreSetting(), coreSetting);

							$this.initializeCoreSettingsWithNames(SettingsService.DEFAULT_SETTINGS_GROUP_NAME, coreSetting);

							if (addToSettingsPopUp) {
								$this._settingsPopUpService.addSettings(false, coreSetting);
							}

							resolve(coreSetting);
						},
						error: (error) => {
							reject(error);
						}
					}
				);
			}
		);
	}

	private readonly _setCoreSettingRequestEndpoint: string = "SetCoreSetting";

	public setCoreSetting(id: number, name: string, value: any, requestOptions?: RequestOptions): Observable<CoreSetting> {
		let url = `settings/${this._setCoreSettingRequestEndpoint}?value=${value}&name=${name}` + (id ? `&id=${id}` : "");

		return this._baseService.post(
			url,
			undefined,
			requestOptions
		);
	}

	private readonly _getCoreSettingsOfSettingGroupRequestEndpoint: string = "GetCoreSettingsOfSettingGroup";

	public getCoreSettingsOfSettingGroup(settingGroupId: number): Promise<CoreSetting[]> {
		let url = `settings/${this._getCoreSettingsOfSettingGroupRequestEndpoint}?settingGroupId=${settingGroupId}`;

		const $this = this;

		return new Promise(
			(resolve, reject) => {
				const observable$: Observable<CoreSetting[]> = this._baseService.get(url);

				observable$.subscribe(
					(response) => {

						$this.initializeCoreSettingsWithNames(SettingsService.DEFAULT_SETTINGS_GROUP_NAME, ...response);

						$this._settingsPopUpService.addSettings(false, ...response);

						resolve(response);
					},
					(error) => {
						reject(error);
					}
				);
			}
		);
	}

	private readonly _getCoreSettingsUnderSelectionSetRequestEndpoint: string = "GetCoreSettingsUnderSelectionSet";

	public getCoreSettingsUnderSelectionSet(selectionSetId: number): Promise<SelectionOptionCoreSettingsGroup> {
		let url = `settings/${this._getCoreSettingsUnderSelectionSetRequestEndpoint}?selectionSetId=${selectionSetId}`;

		const $this = this;

		return new Promise(
			(resolve, reject) => {
				const observable$: Observable<SelectionOptionCoreSettingsGroup> = this._baseService.get(url);

				observable$.subscribe(
					(response) => {

						$this.initializeCoreSettingsWithNames(response.selectionOption.selectionSetName, ...response.coreSettings);

						$this._settingsPopUpService.addSettings(true, ...response.coreSettings);

						resolve(response);
					},
					(error) => {
						reject(error);
					}
				);
			}
		);
	}

	private readonly _getCoreSettingSelectionItemsRequestEndpoint: string = "GetCoreSettingSelectionItems";

	public getCoreSettingSelectionItems(id: number, requestOptions?: RequestOptions): Observable<SelectStatementResult> {
		let url = `settings/${this._getCoreSettingSelectionItemsRequestEndpoint}?id=${id}`;

		return this._baseService.get(url, requestOptions);
	}

	private readonly _getRolesSelectionDataRequestEndpoint: string = "GetRolesSelectionData";

	public getRolesSelectionData(): Observable<TableString[]> {
		let url = `settings/${this._getRolesSelectionDataRequestEndpoint}`;

		return this._baseService.get(url);
	}

	GetRole(roleId: number): Observable<Role[]> {
		let url = 'settings/GetRoles?roleid=' + roleId;
		return this._baseService.post(url);
	}

	UpdateAddRole(role: GroupData[]): Observable<ReturnFlags> {
		let url = 'settings/UpdateAddRole';
		return this._baseService.post(url, role);
	}

	GetSmtpDetail(): Observable<SmtpDetails> {
		let url = 'settings/GetSmtpDetail';
		return this._baseService.post(url);
	}

	UpdateSmtpDetails(data: SmtpDetail): Observable<ReturnFlag> {
		let url = 'settings/UpdateSmtpDetails';
		return this._baseService.post(url, data);
	}

	SaveBookmark(data: BookMark): Observable<ReturnFlag> {
		let url = 'settings/SaveBookmark';
		return this._baseService.post(url, data);
	}

	GetPivotSettingsBookmarks(bookmarkId: Number): Observable<PivotSettingParam[]> {
		let url = `settings/GetPivotSettingsBookmarks?bookmarkId=${bookmarkId}`;
		return this._baseService.get(url);
	}

	LoadBookmark(pagename: string): Observable<BookmarkData[]> {
		let url = 'settings/LoadBookmark?name=' + pagename;
		return this._baseService.post(url);
	}

	UpdateBookmark(dataPage: BookmarkData): Observable<ReturnFlag> {
		let url = 'settings/UpdateBookmark';
		return this._baseService.post(url, dataPage);
	}

	deleteBookmark(dataPage: BookmarkData): Observable<ReturnFlag> {
		let url = 'settings/deleteBookmark';
		return this._baseService.post(url, dataPage);
	}

	ForecastElementAdjust(roleid: number): Observable<ForecastElementDataNew[]> {
		let url = 'settings/ForecastElementAdjust?RoleId=' + roleid;
		return this._baseService.post(url);
	}

	UpdateForecastElementAdjust(data: UpdateForecastElementData): Observable<ReturnFlag> {
		let url = 'settings/UpdateForecastElementAdjust';
		return this._baseService.post(url, data);
	}

	SettingsGroupByName(action: string, name: string, requestOptions?: RequestOptions): Observable<any> {
		let url = 'settings/SettingsGroupByName?action=' + action + '&name=' + name;
		return this._baseService.post(url, null, requestOptions);
	}

	WebMulSettings(action: string, requestOptions?: RequestOptions): Observable<any> {
		let url = 'settings/WebMulSettings?action=' + action;
		return this._baseService.post(url, null, requestOptions);
	}

	GetDDLData(action: string): Observable<SelectionDDL[]> {
		let url = 'settings/GetDDLData?action=' + action;
		return this._baseService.post(url);
	}

	InsetUpdateField(data: NextPreviousOption): Observable<ReturnFlag[]> {
		let url = 'settings/InsetUpdateField';
		return this._baseService.post(url, data);
	}

	GetNextPreList(action: string): Observable<NextPreviousList[]> {
		let url = 'settings/GetNextPreList?action=' + action;
		return this._baseService.post(url);
	}

	RemoveNextPre(id: number): Observable<ReturnFlag> {
		let url = 'settings/RemoveNextPre?id=' + id;
		return this._baseService.post(url);
	}

	SelectNextPreData(data: NextPreAction): Observable<NextPreValue> {
		let url = 'settings/SelectNextPreData';
		return this._baseService.post(url, data);
	}

	SelectPage(action: string): Observable<any> {
		let url = `settings/SelectPage?action=${action}`;
		return this._baseService.post(url);
	}

	SelectDataTypeSelection(action: string): Observable<any> {
		let url = `settings/SelectDataTypeSelection?action=${action}`;
		return this._baseService.post(url);
	}

	storageMapAccessKey = "STKMapAccess";
	async MapAccess(): Promise<string> {
		try {

			if (this.sessionStorageService.getItem(this.storageMapAccessKey)) {
				const now = new Date();
				let times = JSON.parse(this.sessionStorageService.getItem(this.storageMapAccessKey));
				//console.log(now.getTime(),times.expiry,now.getTime()>=times.expiry,now.getTime()<=times.expiry)
				if (now.getTime() <= times.expiry) {
					return times.value;
				}
			}
			let url = `settings/MapAccess`;
			await this._baseService.post(url).toPromise().then(result => {
				let items = result["token"];
				this.sessionStorageService.setItem(this.storageMapAccessKey, items, 60000 * 50);

			});
			let times = JSON.parse(this.sessionStorageService.getItem(this.storageMapAccessKey));
			return times.value;
		}
		catch (error) {
			return "error";
		}
	}

	getListNames(requestOptions?: RequestOptions): Observable<listoptions[]> {
		let url = 'settings/GetListNames';
		return this._baseService.post(
			url,
			undefined,
			requestOptions
		);
	}

	getListElementById(listid: number, requestOptions?: RequestOptions): Observable<any> {
		let url = 'settings/GetListElementById';
		return this._baseService.post(url, listid, requestOptions);
	}

	UpdateListElement(listelement: UpdateListElementData): Observable<ReturnFlags> {
		let url = 'settings/UpdateListElement';
		return this._baseService.post(url, listelement);
	}

	UpdateList(listdata:UpdateListData): Observable<ReturnFlags> {
		let url = 'settings/UpdateList';
		return this._baseService.post(url, listdata);
	}

	DeleteList(listid: number): Observable<ReturnFlags> {
		let url = 'settings/DeleteList';
		return this._baseService.post(url, listid);
	}

	constructor(
		private _settingsPopUpService: SettingsPopUpService,
		private localStorageService: LocalStorageService,
		private sessionStorageService: SessionStorageService
	) {
		if (this._baseService.checkSession()) {
			if (this._coreSettingTypesMap != undefined && !this._coreSettingTypesMap && this.localStorageService.get(this._coreSettingTypesMapCacheName)) {
				this._coreSettingTypesMap = JSON.parse(this.localStorageService.get(this._coreSettingTypesMapCacheName));
			}
			else {
				this.initializeCoreSettingTypesMap();
			}
		}
	}
}

export class BiPivotSettingsDto {
	userId: string;
	response: string;
	pivotsettinglist: PivotSettingParam[]
}

export class UniversalApplicationPage {
	id: number;
	pageName: string;
}

export class SelectionData {
	selectionSets: SelectionSet[];
	selections: Selection[];
	elements: SelectionElement[];

	constructor() {
		this.selectionSets = [];
		this.selections = [];
		this.elements = [];
	}
}

export class SelectionSet {
	selectionSetId: number;
	selectionGroup: string;
	selectionSetName: string;
}

export class Selection implements IReorderable {

	selectionId: number;

	public get uniqueId(): any {
		return this.selectionId;
	}

	listElementId: number;
	selectionName: string;
	isVisible: boolean;
	isMandatory: boolean;
	colWidth: number;
	dataType: string;
	dataTypeFormat: string;

	orderBy: number;

	public get orderId(): number {
		return this.orderBy;
	}
	public set orderId(value: number) {
		this.orderBy = value;
	}

	htmlControlType: number;
	htmlControlTypeName: string;
	searchType: number;
	isDeleted = false;
	caption: string;
	selectionMode: "multiple" | "single";
	aggregateFunctionId: number | null;
	instantSelectionApplication: boolean;
}

export class SelectionElement {
	listElementId: number;
	listElement: string;
	caption: string;
}

export class SelectionOutput {
	selectionId: number;
	selectionSetId: number;
	listElementId: number;
	selectionName: string;
	isVisible: boolean;
	colWidth: number;
	dataType: string;
	dataTypeFormat: string;
	orderBy: number;
	htmlControlType: number;
	searchType: number;
	isDeleted: boolean;
	selectionMode: 'multiple' | 'single';
	aggregateFunctionId: number;
	instantSelectionApplication: boolean;

	constructor(
		selectionId: number,
		selectionSetId: number,
		listElementId: number,
		selectionName: string,
		isVisible: boolean,
		colWidth: number,
		dataType: string,
		dataTypeFormat: string,
		orderBy: number,
		htmlControlType: number,
		searchType: number,
		isDeleted: boolean,
		selectionMode: 'multiple' | 'single',
		aggregateFunctionId: number,
		instantSelectionApplication: boolean,
	) {
		this.selectionId = selectionId;
		this.selectionSetId = selectionSetId;
		this.listElementId = listElementId;
		this.selectionName = selectionName;
		this.isVisible = isVisible;
		this.colWidth = colWidth;
		this.dataType = dataType;
		this.dataTypeFormat = dataTypeFormat;
		this.orderBy = orderBy;
		this.htmlControlType = htmlControlType;
		this.searchType = searchType;
		this.isDeleted = isDeleted;
		this.selectionMode = selectionMode;
		this.aggregateFunctionId = aggregateFunctionId;
		this.instantSelectionApplication = instantSelectionApplication;
	}
}

export class CoreSettingType {
	public Id: number;
	public Name: string;
}

export class CoreSetting {
	public settingId: number;
	public settingGroupId: number;
	public name: string;
	public listId: number;

	public onValueChanged: BehaviorSubject<CoreSetting>;

	private _value: string;
	public get value(): string {
		return this._value;
	}
	public set value(value: string) {
		this._value = value;

		this.onValueChanged.next(this);
	}

	public description: string;

	public settingTypeId: number;
	public settingTypeName: string;

	public sourceGroupName: string;

	public selectionItems: { value: string, displayValue: string }[];

	public initializeSelectionItems(): void {
		if (![0, 3].includes(this.settingTypeId) || (this.selectionItems && this.selectionItems.length > 0)) {
			return;
		}

		const settingsService: SettingsService = ServiceLocator.injector.get(SettingsService);

		settingsService.getCoreSettingSelectionItems(
			this.settingId,
			{
				showLoadingIndicator: false,
				responseLoggingEnabled: false,
			}
		).subscribe(
			{
				next: (selectStatementResult: SelectStatementResult) => {
					this.selectionItems = selectStatementResult?.data;
				}
			}
		);
	}

	/**
	 *
	 */
	constructor() {
		this.onValueChanged = new BehaviorSubject<CoreSetting>(this);
	}
}

export class SelectionOptionCoreSettingsGroup {
	public selectionOption: SelectionOption;
	public coreSettings: CoreSetting[];
}

//TODO: We have SeelectionSet which is pretty much the same. Find a solution where you don't need to duplicate this or describe why it should be a separate class.
export class SelectionOption {
	public selectionGroup: string;
	public selectionSetName: string;
}

export class GeneralSettings {
	settings: GeneralSetting[];
	lists: List[];
}

export class GeneralSetting {
	id: number;
	listId: number | null;
	name: string;
	value: string;
	description: string;
	settingGroupId: number;
	settingGroupName: string;
	settingGroupDescription: string;
	parentSettingGroupId: number;
	parentSettingGroupName: string;
	parentSettingGroupDescription: string;
}

export class GeneralSettingsOutput {
	settingId: number;
	listId: number;
	value: string;
	description: string;

	constructor(settingId: number, listId: number, value: string, description: string) {
		this.settingId = settingId;
		this.listId = listId;
		this.value = value;
		this.description = description;
	}
}

export class RoleMenu {
	menuId: number;
	menuName: string;
	isParent: number;
	isActive: boolean;
	sortingPriority: number;
}

export class MenuItemsByRole {
	menuItems: RoleMenu[];
	roleId: number;

	constructor(menuItems: RoleMenu[], roleId: number) {
		this.menuItems = menuItems;
		this.roleId = roleId;
	}
}

export class ContentItem {
	name: string;
	id: string;
	values: string[];
}

export class ContentItemData {
	name: string;
	id: string;
	values: ItemData[];
}

export class ItemData {
	groupValue: string;
}
export class ContentItemObjects {
	groupObjectName: string;
	groupName: string;
}

export class ContentDynamicItemObjects {
	groupObjectName: string;
	groupName: string;
	captions: string;
	groupType: string;
}

export class ContentPermission {
	groupName: string;
	groupCaptions: string;
	groupValues: string[];
	access: boolean;
}

export class ContentPermissionsByRole {
	permissions: ContentPermission[];
	roleId: number;
	Data: string;
	GroupType: string;

	constructor(permissions: ContentPermission[], roleId: number, Data: string,
		GroupType: string) {
		this.permissions = permissions;
		this.roleId = roleId;
		this.Data = Data;
		this.GroupType = GroupType;

	}
}

export class SmtpDetails {
	default: SmtpDetail;
	newConfig: SmtpDetail;
}

export class SmtpDetail {
	smtpId: number;
	port: number;
	hostName: string;
	enableSsl: boolean;
	useDefaultCredentials: boolean;
	fromMailEmailId: string;
	fromName: string;
	smtpPassword: string;
	isUsed: boolean;
	currentUrl: string;
}

export class SelectionDDL {
	name: string;
	value: string;
}

export class NextPreviousOption {
	PageName: string;
	FieldName: string;
	IsDefault: boolean;
}

export class NextPreviousList {
	name: string;
	value: string;
	isDefault: boolean;
}

export class listoptions {
	listId: number;
	listName: string;
	defaultListElementId?: number
}
export class UpdateListElementData {
	action: string;
	listdata: UpdateListOptions[];
}
export class UpdateListOptions {
	listElementId?: number;
	listElement: string;
	sortOrder?: number;
	listElementGroup?: string;
	listId: number;
}
export class UpdateListData {
	action: string;
	listnewdata: UpdateEditListData[];
}
export class UpdateEditListData {
	listId?: number;
	listName: string;
	defaultListElementId?:number;
}
